import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
  Select,
  MenuItem,
  Grid,
  Stack,
  Button,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormGroup,
} from '@mui/material';
import {
  MyLocation as LocationIcon,
  Public as GlobalIcon,
  FlagCircle as FlagIcon,
  Security as SecurityIcon,
  Description as DocumentIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';

interface DriverSnapshotProps {
  data: any;
  onChange: (data: any) => void;
}

const experienceOptions = [
  {
    value: 'LOCAL',
    label: 'Local (up to 500km)',
    description: 'You have experience driving up to 500km',
  },
  {
    value: 'REGIONAL',
    label: 'Regional (500km+)',
    description: 'You have experience driving over 500km regionally',
  },
  {
    value: 'LONG_HAUL',
    label: 'Long Haul',
    description: 'You have experience with long haul driving',
  },
];

const citizenshipOptions = [
  { value: 'CANADIAN', label: 'Canadian Citizen' },
  { value: 'PERMANENT_RESIDENT', label: 'Permanent Resident' },
  { value: 'WORK_PERMIT', label: 'Work Permit' },
  { value: 'OTHER', label: 'Other' },
];

const canadianProvinces = [
  { value: 'AB', label: 'Alberta' },
  { value: 'BC', label: 'British Columbia' },
  { value: 'MB', label: 'Manitoba' },
  { value: 'NB', label: 'New Brunswick' },
  { value: 'NL', label: 'Newfoundland and Labrador' },
  { value: 'NT', label: 'Northwest Territories' },
  { value: 'NS', label: 'Nova Scotia' },
  { value: 'NU', label: 'Nunavut' },
  { value: 'ON', label: 'Ontario' },
  { value: 'PE', label: 'Prince Edward Island' },
  { value: 'QC', label: 'Quebec' },
  { value: 'SK', label: 'Saskatchewan' },
  { value: 'YT', label: 'Yukon' },
];

const usStates = [
  { value: 'AK', label: 'Alaska' },
  { value: 'AL', label: 'Alabama' },
  { value: 'AR', label: 'Arkansas' },
  { value: 'AZ', label: 'Arizona' },
  { value: 'CA', label: 'California' },
  { value: 'CO', label: 'Colorado' },
  { value: 'CT', label: 'Connecticut' },
  { value: 'DC', label: 'District of Columbia' },
  { value: 'DE', label: 'Delaware' },
  { value: 'FL', label: 'Florida' },
  { value: 'GA', label: 'Georgia' },
  { value: 'HI', label: 'Hawaii' },
  { value: 'IA', label: 'Iowa' },
  { value: 'ID', label: 'Idaho' },
  { value: 'IL', label: 'Illinois' },
  { value: 'IN', label: 'Indiana' },
  { value: 'KS', label: 'Kansas' },
  { value: 'KY', label: 'Kentucky' },
  { value: 'LA', label: 'Louisiana' },
  { value: 'MA', label: 'Massachusetts' },
  { value: 'MD', label: 'Maryland' },
  { value: 'ME', label: 'Maine' },
  { value: 'MI', label: 'Michigan' },
  { value: 'MN', label: 'Minnesota' },
  { value: 'MO', label: 'Missouri' },
  { value: 'MS', label: 'Mississippi' },
  { value: 'MT', label: 'Montana' },
  { value: 'NC', label: 'North Carolina' },
  { value: 'ND', label: 'North Dakota' },
  { value: 'NE', label: 'Nebraska' },
  { value: 'NH', label: 'New Hampshire' },
  { value: 'NJ', label: 'New Jersey' },
  { value: 'NM', label: 'New Mexico' },
  { value: 'NV', label: 'Nevada' },
  { value: 'NY', label: 'New York' },
  { value: 'OH', label: 'Ohio' },
  { value: 'OK', label: 'Oklahoma' },
  { value: 'OR', label: 'Oregon' },
  { value: 'PA', label: 'Pennsylvania' },
  { value: 'RI', label: 'Rhode Island' },
  { value: 'SC', label: 'South Carolina' },
  { value: 'SD', label: 'South Dakota' },
  { value: 'TN', label: 'Tennessee' },
  { value: 'TX', label: 'Texas' },
  { value: 'UT', label: 'Utah' },
  { value: 'VA', label: 'Virginia' },
  { value: 'VT', label: 'Vermont' },
  { value: 'WA', label: 'Washington' },
  { value: 'WI', label: 'Wisconsin' },
  { value: 'WV', label: 'West Virginia' },
  { value: 'WY', label: 'Wyoming' },
];

const DriverSnapshot: React.FC<DriverSnapshotProps> = ({ data, onChange }) => {
  const [openDialog, setOpenDialog] = useState<string | null>(null);

  const updateData = (field: string, value: any) => {
    onChange({
      ...data,
      [field]: value,
    });
  };

  const handleDialogOpen = (dialogType: string) => {
    setOpenDialog(dialogType);
  };

  const handleDialogClose = () => {
    setOpenDialog(null);
  };

  const SnapshotItem = ({
    title,
    description,
    icon: Icon,
    onEdit,
    status = 'pending',
    chipText,
  }: {
    title: string;
    description: string;
    icon: any;
    onEdit: () => void;
    status?: 'pending' | 'complete' | 'warning';
    chipText?: string;
  }) => {
    const getStatusColor = () => {
      switch (status) {
        case 'complete':
          return '#2e7d32';
        case 'warning':
          return '#ed6c02';
        default:
          return '#757575';
      }
    };

    return (
      <Card
        variant="outlined"
        sx={{
          mb: 2,
          borderColor: status === 'complete' ? '#2e7d32' : '#e0e0e0',
          '&:hover': {
            borderColor: '#2e7d32',
            cursor: 'pointer',
          },
        }}
        onClick={onEdit}
      >
        <CardContent sx={{ p: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" spacing={2} flex={1}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  backgroundColor: '#f5f5f5',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon sx={{ color: getStatusColor() }} />
              </Box>
              <Box flex={1}>
                <Typography variant="h6" fontWeight="600" gutterBottom>
                  {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {description}
                </Typography>
                {chipText && (
                  <Chip
                    label={chipText}
                    size="small"
                    sx={{
                      mt: 1,
                      backgroundColor: status === 'complete' ? '#e8f5e8' : '#f5f5f5',
                      color: getStatusColor(),
                    }}
                  />
                )}
              </Box>
            </Stack>
            <Button
              variant="contained"
              size="small"
              sx={{
                backgroundColor: '#1976d2',
                '&:hover': { backgroundColor: '#1565c0' },
                borderRadius: 1,
              }}
            >
              {status === 'complete' ? 'Edit' : 'Open'}
            </Button>
          </Stack>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        Complete your driver snapshot to showcase your experience and qualifications
      </Typography>

      <Stack spacing={3} mt={3}>
        {/* Radius of Experience */}
        <SnapshotItem
          title="Radius of Experience"
          description="Select your driving experience range"
          icon={LocationIcon}
          onEdit={() => handleDialogOpen('radius')}
          status={data.radiusOfExperience ? 'complete' : 'pending'}
          chipText={
            data.radiusOfExperience
              ? experienceOptions.find(opt => opt.value === data.radiusOfExperience)?.label
              : undefined
          }
        />

        {/* Canada Experience */}
        <SnapshotItem
          title="Canada Experience"
          description="Your experience operating a vehicle in Canada"
          icon={FlagIcon}
          onEdit={() => handleDialogOpen('canada')}
          status={data.canadaExperience ? 'complete' : 'pending'}
          chipText={
            data.canadaExperience?.hasExperience === false
              ? 'No Canada experience'
              : data.canadaExperience?.provinces?.length > 0
              ? `Experience in ${data.canadaExperience.provinces.length} province${
                  data.canadaExperience.provinces.length > 1 ? 's' : ''
                }`
              : undefined
          }
        />

        {/* USA Experience */}
        <SnapshotItem
          title="USA Experience"
          description="Your experience operating a vehicle in the United States"
          icon={GlobalIcon}
          onEdit={() => handleDialogOpen('usa')}
          status={data.usaExperience ? 'complete' : 'pending'}
          chipText={
            data.usaExperience?.hasExperience === false
              ? 'No USA experience'
              : data.usaExperience?.states?.length > 0
              ? `Experience in ${data.usaExperience.states.length} state${
                  data.usaExperience.states.length > 1 ? 's' : ''
                }`
              : undefined
          }
        />

        {/* International Experience */}
        <SnapshotItem
          title="International Experience"
          description="Your experience operating vehicles internationally"
          icon={GlobalIcon}
          onEdit={() => handleDialogOpen('international')}
          status={data.internationalExperience ? 'complete' : 'pending'}
          chipText={
            data.internationalExperience?.hasExperience === false
              ? 'No international experience'
              : data.internationalExperience?.hasExperience === true
              ? 'International experience recorded'
              : undefined
          }
        />

        {/* Safety History */}
        <SnapshotItem
          title="Safety History"
          description="Your recent accident and ticket history"
          icon={WarningIcon}
          onEdit={() => handleDialogOpen('safety')}
          status={data.safetyHistory ? 'complete' : 'pending'}
          chipText={
            data.safetyHistory?.accidents === false && data.safetyHistory?.tickets === false
              ? 'No recent accidents or tickets'
              : data.safetyHistory
              ? 'Safety history recorded'
              : undefined
          }
        />

        {/* Citizenship & Documents */}
        <SnapshotItem
          title="Citizenship & Documents"
          description="Your citizenship status and travel documents"
          icon={DocumentIcon}
          onEdit={() => handleDialogOpen('citizenship')}
          status={data.citizenshipDocuments ? 'complete' : 'pending'}
          chipText={
            data.citizenshipDocuments?.citizenship && data.citizenshipDocuments?.passport
              ? 'Citizenship and passport documented'
              : data.citizenshipDocuments
              ? 'Partially complete'
              : undefined
          }
        />

        {/* US Entry */}
        <SnapshotItem
          title="U.S. Entry"
          description="Your US entry status and history"
          icon={FlagIcon}
          onEdit={() => handleDialogOpen('us-entry')}
          status={data.usEntry ? 'complete' : 'pending'}
          chipText={
            data.usEntry?.canEnter === true
              ? 'Able to enter US'
              : data.usEntry?.canEnter === false
              ? 'Cannot enter US'
              : undefined
          }
        />

        {/* Commercial Numbers */}
        <SnapshotItem
          title="Commercial Numbers"
          description="Your FAST Card, US DOT, and MC Numbers"
          icon={DocumentIcon}
          onEdit={() => handleDialogOpen('commercial-numbers')}
          status={data.commercialNumbers ? 'complete' : 'pending'}
          chipText={data.commercialNumbers ? 'Commercial numbers recorded' : undefined}
        />

        {/* Drug and Alcohol Testing */}
        <SnapshotItem
          title="Drug and Alcohol Testing"
          description="Your drug and alcohol testing compliance"
          icon={SecurityIcon}
          onEdit={() => handleDialogOpen('testing')}
          status={data.drugAndAlcoholTesting ? 'complete' : 'pending'}
          chipText={
            data.drugAndAlcoholTesting === false ? 'No complications in past 3 years' : undefined
          }
        />
      </Stack>

      {/* Experience Radius Dialog */}
      <Dialog open={openDialog === 'radius'} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Radius of Experience</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Select your typical driving experience range
          </Typography>
          <FormControl component="fieldset" fullWidth sx={{ mt: 2 }}>
            <RadioGroup
              value={data.radiusOfExperience || ''}
              onChange={e => updateData('radiusOfExperience', e.target.value)}
            >
              {experienceOptions.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body1">{option.label}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ mb: 1, alignItems: 'flex-start' }}
                />
              ))}
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button variant="contained" onClick={handleDialogClose}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Canada Experience Dialog */}
      <Dialog open={openDialog === 'canada'} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>Canada Experience</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Have you operated a vehicle in Canada?
          </Typography>
          <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
            <Select
              value={
                data.canadaExperience?.hasExperience === true
                  ? 'true'
                  : data.canadaExperience?.hasExperience === false
                  ? 'false'
                  : ''
              }
              onChange={e =>
                updateData('canadaExperience', {
                  ...data.canadaExperience,
                  hasExperience: e.target.value === 'true',
                  provinces:
                    e.target.value === 'true' ? data.canadaExperience?.provinces || [] : [],
                })
              }
              displayEmpty
            >
              <MenuItem value="">Select an option</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>

          {data.canadaExperience?.hasExperience === true && (
            <Box>
              <Typography variant="body1" gutterBottom sx={{ fontWeight: 600 }}>
                Mark all provinces you have experience with in Canada.
              </Typography>
              <FormGroup sx={{ mt: 2 }}>
                <Grid container spacing={1}>
                  {canadianProvinces.map(province => (
                    <Grid item xs={12} sm={6} md={4} key={province.value}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              data.canadaExperience?.provinces?.includes(province.value) || false
                            }
                            onChange={e => {
                              const currentProvinces = data.canadaExperience?.provinces || [];
                              const newProvinces = e.target.checked
                                ? [...currentProvinces, province.value]
                                : currentProvinces.filter(p => p !== province.value);
                              updateData('canadaExperience', {
                                ...data.canadaExperience,
                                provinces: newProvinces,
                              });
                            }}
                            color="primary"
                          />
                        }
                        label={province.label}
                      />
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
          <Button variant="contained" onClick={handleDialogClose}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* USA Experience Dialog */}
      <Dialog open={openDialog === 'usa'} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>USA Experience</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Have you operated a vehicle in the United States of America?
          </Typography>
          <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
            <Select
              value={
                data.usaExperience?.hasExperience === true
                  ? 'true'
                  : data.usaExperience?.hasExperience === false
                  ? 'false'
                  : ''
              }
              onChange={e =>
                updateData('usaExperience', {
                  ...data.usaExperience,
                  hasExperience: e.target.value === 'true',
                  states: e.target.value === 'true' ? data.usaExperience?.states || [] : [],
                })
              }
              displayEmpty
            >
              <MenuItem value="">Select an option</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>

          {data.usaExperience?.hasExperience === true && (
            <Box>
              <Typography variant="body1" gutterBottom sx={{ fontWeight: 600 }}>
                Mark all states you have experience with in United States of America.
              </Typography>
              <Box
                sx={{
                  maxHeight: 300,
                  overflow: 'auto',
                  mt: 2,
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 1,
                }}
              >
                <FormGroup>
                  <Grid container spacing={0}>
                    {usStates.map(state => (
                      <Grid item xs={12} sm={6} md={4} key={state.value}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={data.usaExperience?.states?.includes(state.value) || false}
                              onChange={e => {
                                const currentStates = data.usaExperience?.states || [];
                                const newStates = e.target.checked
                                  ? [...currentStates, state.value]
                                  : currentStates.filter(s => s !== state.value);
                                updateData('usaExperience', {
                                  ...data.usaExperience,
                                  states: newStates,
                                });
                              }}
                              color="primary"
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">{state.label}</Typography>}
                          sx={{ margin: 0, width: '100%' }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </FormGroup>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                Scroll for more states
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
          <Button variant="contained" onClick={handleDialogClose}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Safety History Dialog */}
      <Dialog open={openDialog === 'safety'} onClose={handleDialogClose} maxWidth="md" fullWidth>
        <DialogTitle>Safety History</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Please provide information about your recent safety record
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Accident History
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Have you been involved in an accident in the past three years?
            </Typography>
            <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
              <Select
                value={
                  data.safetyHistory?.accidents === true
                    ? 'true'
                    : data.safetyHistory?.accidents === false
                    ? 'false'
                    : ''
                }
                onChange={e =>
                  updateData('safetyHistory', {
                    ...data.safetyHistory,
                    accidents: e.target.value === 'true',
                    accidentDescription:
                      e.target.value === 'true'
                        ? data.safetyHistory?.accidentDescription || ''
                        : '',
                  })
                }
                displayEmpty
              >
                <MenuItem value="">Select an option</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>

            {data.safetyHistory?.accidents === true && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Describe each accident you were involved in the past three years"
                value={data.safetyHistory?.accidentDescription || ''}
                onChange={e =>
                  updateData('safetyHistory', {
                    ...data.safetyHistory,
                    accidentDescription: e.target.value,
                  })
                }
                sx={{ mb: 3 }}
              />
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Ticket History
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Have you received any tickets in the past three years?
            </Typography>
            <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
              <Select
                value={
                  data.safetyHistory?.tickets === true
                    ? 'true'
                    : data.safetyHistory?.tickets === false
                    ? 'false'
                    : ''
                }
                onChange={e =>
                  updateData('safetyHistory', {
                    ...data.safetyHistory,
                    tickets: e.target.value === 'true',
                    ticketDescription:
                      e.target.value === 'true' ? data.safetyHistory?.ticketDescription || '' : '',
                  })
                }
                displayEmpty
              >
                <MenuItem value="">Select an option</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>

            {data.safetyHistory?.tickets === true && (
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Describe each ticket you received in the past three years"
                value={data.safetyHistory?.ticketDescription || ''}
                onChange={e =>
                  updateData('safetyHistory', {
                    ...data.safetyHistory,
                    ticketDescription: e.target.value,
                  })
                }
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
          <Button variant="contained" onClick={handleDialogClose}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* International Experience Dialog */}
      <Dialog
        open={openDialog === 'international'}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>International Experience</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Do you have experience operating a commercial vehicle outside of Canada and the U.S.A.?
          </Typography>
          <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
            <Select
              value={
                data.internationalExperience?.hasExperience === true
                  ? 'true'
                  : data.internationalExperience?.hasExperience === false
                  ? 'false'
                  : ''
              }
              onChange={e =>
                updateData('internationalExperience', {
                  ...data.internationalExperience,
                  hasExperience: e.target.value === 'true',
                  description:
                    e.target.value === 'true'
                      ? data.internationalExperience?.description || ''
                      : '',
                })
              }
              displayEmpty
            >
              <MenuItem value="">Select an option</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>

          {data.internationalExperience?.hasExperience === true && (
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Please describe any vehicle operating experience you have outside Canada and the USA."
              value={data.internationalExperience?.description || ''}
              onChange={e =>
                updateData('internationalExperience', {
                  ...data.internationalExperience,
                  description: e.target.value,
                })
              }
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
          <Button variant="contained" onClick={handleDialogClose}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Drug and Alcohol Testing Dialog */}
      <Dialog open={openDialog === 'testing'} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Drug and Alcohol Testing</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" fullWidth>
            <RadioGroup
              value={
                data.drugAndAlcoholTesting === true
                  ? 'true'
                  : data.drugAndAlcoholTesting === false
                  ? 'false'
                  : ''
              }
              onChange={e => {
                updateData('drugAndAlcoholTesting', e.target.value === 'true');
              }}
            >
              <FormControlLabel value="true" control={<Radio />} label="Yes" />
              <FormControlLabel value="false" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button variant="contained" onClick={handleDialogClose}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Citizenship & Documents Dialog */}
      <Dialog
        open={openDialog === 'citizenship'}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Citizenship & Documents</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Please provide your citizenship status and passport information
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              Citizenship
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Are you a Canadian citizen?
            </Typography>
            <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
              <Select
                value={data.citizenshipDocuments?.citizenship || ''}
                onChange={e =>
                  updateData('citizenshipDocuments', {
                    ...data.citizenshipDocuments,
                    citizenship: e.target.value,
                  })
                }
                displayEmpty
              >
                <MenuItem value="">Select an option</MenuItem>
                {citizenshipOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              Passport
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Do you have a valid passport?
            </Typography>
            <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
              <Select
                value={
                  data.citizenshipDocuments?.hasPassport === true
                    ? 'true'
                    : data.citizenshipDocuments?.hasPassport === false
                    ? 'false'
                    : ''
                }
                onChange={e =>
                  updateData('citizenshipDocuments', {
                    ...data.citizenshipDocuments,
                    hasPassport: e.target.value === 'true',
                    passportCountry:
                      e.target.value === 'true'
                        ? data.citizenshipDocuments?.passportCountry || ''
                        : '',
                    passportExpiryYear:
                      e.target.value === 'true'
                        ? data.citizenshipDocuments?.passportExpiryYear || ''
                        : '',
                    passportExpiryMonth:
                      e.target.value === 'true'
                        ? data.citizenshipDocuments?.passportExpiryMonth || ''
                        : '',
                    passportExpiryDay:
                      e.target.value === 'true'
                        ? data.citizenshipDocuments?.passportExpiryDay || ''
                        : '',
                  })
                }
                displayEmpty
              >
                <MenuItem value="">Select an option</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>

            {data.citizenshipDocuments?.hasPassport === true && (
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Which country issued your passport?
                </Typography>
                <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
                  <Select
                    value={data.citizenshipDocuments?.passportCountry || ''}
                    onChange={e =>
                      updateData('citizenshipDocuments', {
                        ...data.citizenshipDocuments,
                        passportCountry: e.target.value,
                      })
                    }
                    displayEmpty
                  >
                    <MenuItem value="">Select country</MenuItem>
                    <MenuItem value="Canada">Canada</MenuItem>
                    <MenuItem value="United States">United States</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>

                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Expiry Date
                </Typography>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <Select
                        value={data.citizenshipDocuments?.passportExpiryYear || ''}
                        onChange={e =>
                          updateData('citizenshipDocuments', {
                            ...data.citizenshipDocuments,
                            passportExpiryYear: e.target.value,
                          })
                        }
                        displayEmpty
                      >
                        <MenuItem value="">Year</MenuItem>
                        {Array.from({ length: 20 }, (_, i) => new Date().getFullYear() + i).map(
                          year => (
                            <MenuItem key={year} value={year.toString()}>
                              {year}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <Select
                        value={data.citizenshipDocuments?.passportExpiryMonth || ''}
                        onChange={e =>
                          updateData('citizenshipDocuments', {
                            ...data.citizenshipDocuments,
                            passportExpiryMonth: e.target.value,
                          })
                        }
                        displayEmpty
                      >
                        <MenuItem value="">Month</MenuItem>
                        {[
                          'January',
                          'February',
                          'March',
                          'April',
                          'May',
                          'June',
                          'July',
                          'August',
                          'September',
                          'October',
                          'November',
                          'December',
                        ].map((month, _index) => (
                          <MenuItem key={month} value={month}>
                            {month}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={4}>
                    <FormControl fullWidth>
                      <Select
                        value={data.citizenshipDocuments?.passportExpiryDay || ''}
                        onChange={e =>
                          updateData('citizenshipDocuments', {
                            ...data.citizenshipDocuments,
                            passportExpiryDay: e.target.value,
                          })
                        }
                        displayEmpty
                      >
                        <MenuItem value="">Day</MenuItem>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                          <MenuItem key={day} value={day.toString()}>
                            {day}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
          <Button variant="contained" onClick={handleDialogClose}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* US Entry Dialog */}
      <Dialog open={openDialog === 'us-entry'} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>U.S. Entry</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Please provide information about your US entry status
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Have you ever been denied entry into the US?
            </Typography>
            <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
              <Select
                value={
                  data.usEntry?.deniedEntry === true
                    ? 'true'
                    : data.usEntry?.deniedEntry === false
                    ? 'false'
                    : ''
                }
                onChange={e =>
                  updateData('usEntry', {
                    ...data.usEntry,
                    deniedEntry: e.target.value === 'true',
                  })
                }
                displayEmpty
              >
                <MenuItem value="">Select an option</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Are you now able to enter the US?
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <Select
                value={
                  data.usEntry?.canEnter === true
                    ? 'true'
                    : data.usEntry?.canEnter === false
                    ? 'false'
                    : ''
                }
                onChange={e =>
                  updateData('usEntry', {
                    ...data.usEntry,
                    canEnter: e.target.value === 'true',
                  })
                }
                displayEmpty
              >
                <MenuItem value="">Select answer...</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
          <Button variant="contained" onClick={handleDialogClose}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Commercial Numbers Dialog */}
      <Dialog
        open={openDialog === 'commercial-numbers'}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Commercial Numbers</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Please provide your commercial transportation numbers
          </Typography>

          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" gutterBottom>
              FAST Card
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Do you have a FAST card?
            </Typography>
            <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
              <Select
                value={
                  data.commercialNumbers?.hasFastCard === true
                    ? 'true'
                    : data.commercialNumbers?.hasFastCard === false
                    ? 'false'
                    : ''
                }
                onChange={e =>
                  updateData('commercialNumbers', {
                    ...data.commercialNumbers,
                    hasFastCard: e.target.value === 'true',
                    fastCardNumber:
                      e.target.value === 'true' ? data.commercialNumbers?.fastCardNumber || '' : '',
                  })
                }
                displayEmpty
              >
                <MenuItem value="">Select an option</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>

            {data.commercialNumbers?.hasFastCard === true && (
              <TextField
                fullWidth
                label="Enter your FAST card number"
                value={data.commercialNumbers?.fastCardNumber || ''}
                onChange={e =>
                  updateData('commercialNumbers', {
                    ...data.commercialNumbers,
                    fastCardNumber: e.target.value,
                  })
                }
                sx={{ mb: 3 }}
              />
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              U.S. D.O.T. Number
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Do you have a U.S. D.O.T. Number?
            </Typography>
            <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
              <Select
                value={
                  data.commercialNumbers?.hasUsDotNumber === true
                    ? 'true'
                    : data.commercialNumbers?.hasUsDotNumber === false
                    ? 'false'
                    : ''
                }
                onChange={e =>
                  updateData('commercialNumbers', {
                    ...data.commercialNumbers,
                    hasUsDotNumber: e.target.value === 'true',
                    usDotNumber:
                      e.target.value === 'true' ? data.commercialNumbers?.usDotNumber || '' : '',
                  })
                }
                displayEmpty
              >
                <MenuItem value="">Select an option</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>

            {data.commercialNumbers?.hasUsDotNumber === true && (
              <TextField
                fullWidth
                label="Enter your U.S. D.O.T. Number"
                value={data.commercialNumbers?.usDotNumber || ''}
                onChange={e =>
                  updateData('commercialNumbers', {
                    ...data.commercialNumbers,
                    usDotNumber: e.target.value,
                  })
                }
                sx={{ mb: 3 }}
              />
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="h6" gutterBottom>
              MC Number
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Do you have an MC Number?
            </Typography>
            <FormControl fullWidth sx={{ mt: 2, mb: 3 }}>
              <Select
                value={
                  data.commercialNumbers?.hasMcNumber === true
                    ? 'true'
                    : data.commercialNumbers?.hasMcNumber === false
                    ? 'false'
                    : ''
                }
                onChange={e =>
                  updateData('commercialNumbers', {
                    ...data.commercialNumbers,
                    hasMcNumber: e.target.value === 'true',
                    mcNumber:
                      e.target.value === 'true' ? data.commercialNumbers?.mcNumber || '' : '',
                  })
                }
                displayEmpty
              >
                <MenuItem value="">Select an option</MenuItem>
                <MenuItem value="true">Yes</MenuItem>
                <MenuItem value="false">No</MenuItem>
              </Select>
            </FormControl>

            {data.commercialNumbers?.hasMcNumber === true && (
              <TextField
                fullWidth
                label="Enter your MC Number"
                value={data.commercialNumbers?.mcNumber || ''}
                onChange={e =>
                  updateData('commercialNumbers', {
                    ...data.commercialNumbers,
                    mcNumber: e.target.value,
                  })
                }
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Close</Button>
          <Button variant="contained" onClick={handleDialogClose}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DriverSnapshot;
